import { useState } from 'react';
import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cx } from '../lib/cx';
import { useFormat } from '../lib/format';

/** A single star filled in proportion to the rating, followed by the number. */
export function RatingBadge({ avg, count, className }: { avg: number; count: number; className?: string }) {
  const { t } = useTranslation();
  const format = useFormat();
  const fill = count ? Math.max(0, Math.min(1, avg / 5)) : 0;
  const label = count ? t('rating.label', { rating: format.number(avg), count }) : t('rating.none');
  return (
    <span className={cx('inline-flex items-center gap-1 text-sm', className)} title={label}>
      <span className="relative inline-block size-[1.1em]" aria-hidden>
        <Star className="absolute inset-0 size-full text-star" strokeWidth={1.75} />
        <span className="absolute inset-y-0 start-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
          <Star className="size-[1.1em] fill-star text-star" strokeWidth={1.75} />
        </span>
      </span>
      <span className="sr-only">{label}</span>
      <span aria-hidden className="font-semibold tabular-nums">
        {count ? format.number(avg) : '–'}
      </span>
      {count > 0 && (
        <span aria-hidden className="text-muted tabular-nums">
          ({count})
        </span>
      )}
    </span>
  );
}

interface StarInputProps {
  value: number;
  onChange: (stars: number) => void;
  disabled?: boolean;
  size?: 'md' | 'lg';
}

/** Five clickable stars. */
export function StarInput({ value, onChange, disabled, size = 'lg' }: StarInputProps) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div role="radiogroup" aria-label={t('recipe.rateThis')} className="inline-flex" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((stars) => (
        <button
          key={stars}
          type="button"
          role="radio"
          aria-checked={value === stars}
          aria-label={t('rating.rateStars', { count: stars })}
          disabled={disabled}
          onMouseEnter={() => setHover(stars)}
          onFocus={() => setHover(stars)}
          onBlur={() => setHover(0)}
          onClick={() => onChange(stars)}
          className="rounded-full p-1 transition-transform hover:scale-110 disabled:hover:scale-100"
        >
          <Star
            className={cx(size === 'lg' ? 'size-9' : 'size-6', stars <= shown ? 'fill-star text-star' : 'text-muted/60')}
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
}
